import React, { useEffect, useRef, useState } from 'react';
import {
  Bell,
  WifiOff,
  Wifi,
  MonitorX,
  Monitor,
  Cpu,
  CalendarClock,
  FilePlus,
  AlertTriangle,
  X,
  Trash2,
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { useCity } from '../contexts/CityContext';

const SEVERITY_COLOR = {
  info: '#3b82f6',
  success: '#10b981',
  warning: '#c8a147',
  critical: '#ef4444',
};

// Per-type icon. Falls back to a severity-appropriate generic icon.
function iconForType(type, severity) {
  switch (type) {
    case 'screen-offline':
      return WifiOff;
    case 'screen-online':
      return Wifi;
    case 'screen-pending':
      return Monitor;
    case 'resolution-changed':
      return Monitor;
    case 'resolution-mismatch':
      return MonitorX;
    case 'hardware-changed':
      return Cpu;
    case 'schedule-updated':
    case 'schedule-cancelled':
      return CalendarClock;
    case 'content-added':
      return FilePlus;
    default:
      return severity === 'critical' || severity === 'warning' ? AlertTriangle : Bell;
  }
}

function formatRelativeTime(ts) {
  const diff = Date.now() - ts;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  try {
    return new Date(ts).toLocaleDateString();
  } catch {
    return '';
  }
}

const NotificationBell = () => {
  const { currentCity } = useCity();
  const { notifications, unreadCount, markAllRead, remove, clearAll } = useNotifications(currentCity);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      // Opening the panel clears the unread badge — the user has seen them.
      if (next && unreadCount > 0) markAllRead();
      return next;
    });
  };

  const badge = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={toggle}
        aria-label={unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        aria-haspopup="true"
        aria-expanded={open}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          border: '1px solid var(--border-color)',
          borderRadius: 8,
          background: open ? 'rgba(200, 161, 71, 0.12)' : 'transparent',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          padding: 0,
          transition: 'background 0.15s',
        }}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 16,
              height: 16,
              padding: '0 4px',
              borderRadius: 8,
              backgroundColor: '#ef4444',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              lineHeight: '16px',
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            width: 380,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 460,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
            border: '1px solid var(--border-color)',
            borderRadius: 12,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
            zIndex: 50,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid var(--border-color)',
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
              Notifications
            </span>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  border: 'none',
                  background: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: '4px 6px',
                  borderRadius: 6,
                }}
              >
                <Trash2 size={13} /> Clear all
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {notifications.length === 0 ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  padding: '40px 16px',
                  color: 'var(--text-secondary)',
                }}
              >
                <Bell size={28} style={{ opacity: 0.4 }} />
                <span style={{ fontSize: 13 }}>You're all caught up</span>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = iconForType(n.type, n.severity);
                const color = SEVERITY_COLOR[n.severity] || SEVERITY_COLOR.info;
                return (
                  <div
                    key={n.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 10,
                      padding: '12px 16px',
                      borderBottom: '1px solid #f1f5f9',
                      backgroundColor: n.read ? '#fff' : 'rgba(200, 161, 71, 0.06)',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 30,
                        height: 30,
                        borderRadius: 8,
                        flexShrink: 0,
                        backgroundColor: `${color}1a`,
                        color,
                      }}
                    >
                      <Icon size={16} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          lineHeight: 1.3,
                        }}
                      >
                        {n.title}
                      </div>
                      {n.message && (
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--text-secondary)',
                            marginTop: 2,
                            wordBreak: 'break-word',
                          }}
                        >
                          {n.message}
                        </div>
                      )}
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                        {formatRelativeTime(n.timestamp)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(n.id)}
                      aria-label="Dismiss notification"
                      style={{
                        border: 'none',
                        background: 'none',
                        color: '#cbd5e1',
                        cursor: 'pointer',
                        padding: 2,
                        flexShrink: 0,
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
